// app/src/features/chat/ChatPanel.jsx
import React from "react";
import ChatContent from "../../components/ChatContent.jsx";
import ChatPanelHeader from "../../components/ChatPanelHeader.jsx";
import LoadingLine from "../../components/LoadingLine.jsx";
import { OverlayPanel } from "../../components/Panels.jsx";
import { C } from "../../lib/colors.js";
import { DRAFT_CONV_ID } from "../../lib/forecast/draft.js";
import { shouldShowIntroduction } from "../../lib/forecast/restore.js";
import {
  selectChatProjection,
  selectComparisonChatProjection,
} from "../../lib/forecast/selectors.js";
import { forecasterDisplayName } from "../../lib/forecast/engines.js";
import { RAIL_SECTIONS } from "../../lib/navRail.js";

// Shown while bootPhase !== 'ready', so the stale introduction does not flash before the restored
// forecast is displayed.
function NeutralRestoringShell() {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: C.gray,
        fontSize: 12,
      }}
    >
      <LoadingLine>Restoring your forecasts…</LoadingLine>
    </div>
  );
}

/**
 * The frame's `chatOverlay` slot: the left-side chat drawer with its header (the rail heading and
 * the clear-history control), the resize handle and the chat content — the active forecast's
 * projection (both legs' under compare), or the draft's entries pre-forecast behind the boot gate.
 * A phone never renders the panel's content (`AppFrame` mounts `PhoneApp` alone), and the
 * `isPhone` guard inside keeps `chatJournal.view` from recording on one.
 */
export default function ChatPanel({ ctx }) {
  const {
    active,
    activeTrace,
    askHeroOn,
    bootPhase,
    cancellableRun,
    chatJournal,
    chatOpen,
    chatThinking,
    compareOn,
    compareRoots,
    drawerZ,
    endPostTour,
    endTourBtn,
    enterFlash,
    forecastEngine,
    forecastLiveProgressEnabled,
    handleChatMessage,
    handleEndTourBtn,
    handleSelectTier,
    healthError,
    input,
    isPhone,
    listening,
    loading,
    onCancelForecast,
    openReport,
    orchestrators,
    pendingRestore,
    postTourActive,
    proColors,
    quickDeepSelectorAllowed,
    quickDeepToggleAllowed,
    railResizeHandle,
    railSwitched,
    recognitionRef,
    resetForecast,
    retryHealth,
    runState,
    runStatusOn,
    setForecastEngine,
    setInput,
    setListening,
    setSpeaking,
    showReport,
    signalTourAction,
    skipTour,
    speaking,
    suppressGuideNarration,
    theme,
    tourActive,
    tourExamples,
    tourWaitKey,
    voiceMode,
  } = ctx;
  return (
    <OverlayPanel
      side="left"
      open={chatOpen}
      // The CSS variable, not the state: it moves live during a resize drag.
      width="var(--rail-panel-w)"
      instant={railSwitched}
      // Uses its side-drawer slot (useDrawerStack), except during the tour's report step,
      // which lifts the chat above the report modal so its guidance stays readable.
      zOverride={tourActive && showReport ? 450 : drawerZ.chat}
      data-testid="sayer-chat"
      surface="chat"
      surfaceKey={active?.id}
    >
      {/* The timeline slides with the panel edge while dragging and re-frames on release
         (useNavRailPanelWidth). */}
      <ChatPanelHeader
        label={RAIL_SECTIONS[1].heading}
        showHeading
        canClear={chatJournal.hasHistory}
        onClear={chatJournal.clear}
      />
      {chatOpen && (
        <div
          data-chat-resize=""
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize chat"
          title="Drag to resize · double-click to reset"
          {...railResizeHandle}
          className="rail-resize-handle"
        />
      )}
      {/* The result line is projected from runsById, never stored as a message.
         Pre-forecast, the DRAFT_CONV_ID conversation entries are projected as plain
         entry items (no run → no event lines), so the example-question empty-state
         still shows when the draft is empty. */}
      {(() => {
        // A phone never renders this panel, and `chatJournal.view` records what it is handed
        // into the persisted journal (useChatJournal) — so none of this may run on a phone.
        if (isPhone) return null;
        let chatItems;
        if (active?.id) {
          // Compare mode merges BOTH legs' projections so each leg's running placeholder /
          // contract / result line shows (engine-attributed), not just the active leg's.
          chatItems = compareOn
            ? selectComparisonChatProjection(
                runState,
                compareRoots.map((r) => r.id),
                forecasterDisplayName,
              )
            : selectChatProjection(runState, active.id, {
                deepPanel: forecastLiveProgressEnabled,
              });
        } else {
          // No active forecast — gate on boot phase to avoid flashing the
          // introduction while a restore is in progress.
          const draftConv = runState.conversationsById[DRAFT_CONV_ID];
          const draftEntries = draftConv?.entries ?? [];
          const showIntro = shouldShowIntroduction({
            bootPhase,
            hasActive: !!active?.id,
            pendingRestore,
          });
          if (!showIntro) return <NeutralRestoringShell />;
          chatItems = draftEntries.map((e) => ({
            kind: "entry",
            role: e.role,
            entryKind: e.kind,
            content: e.content,
            ts: e.ts,
            metadata: e.metadata,
          }));
        }
        // Dropping the tour's scripted narration (entryKind "guide") stops a refresh from
        // resurrecting the end-of-tour coaching from the re-selected example forecast's
        // persisted thread (or a leftover draft).
        if (suppressGuideNarration) chatItems = chatItems.filter((it) => it.entryKind !== "guide");
        return (
          <ChatContent
            theme={theme}
            items={chatJournal.view(chatItems)}
            onOpenForecast={chatJournal.openForecast}
            panelOpen={chatOpen}
            input={input}
            setInput={setInput}
            onSubmit={handleChatMessage}
            onReset={() => {
              chatJournal.newQuestion();
              resetForecast();
            }}
            onOpenReport={() => openReport(true)}
            loading={loading}
            chatThinking={chatThinking}
            forecast={active}
            forecastEngine={forecastEngine}
            setForecastEngine={setForecastEngine}
            healthError={healthError}
            onRetryHealth={retryHealth}
            onSelectTier={handleSelectTier}
            quickDeepEnabled={quickDeepToggleAllowed}
            quickDeepSelectorEnabled={quickDeepSelectorAllowed}
            orchestrators={orchestrators}
            voiceMode={voiceMode}
            listening={listening}
            setListening={setListening}
            speaking={speaking}
            setSpeaking={setSpeaking}
            recognitionRef={recognitionRef}
            proColors={proColors}
            activeTrace={activeTrace}
            canCancel={!!cancellableRun}
            onCancelForecast={onCancelForecast}
            askHeroShowing={askHeroOn}
            runStatusShowing={runStatusOn}
            onSkipTour={skipTour}
            tourActive={tourActive}
            tourWaitKey={tourWaitKey}
            onTourNext={() => signalTourAction(tourWaitKey)}
            tourExamples={tourExamples}
            enterFlash={enterFlash}
            endTourBtn={endTourBtn}
            onEndTourBtn={handleEndTourBtn}
            postTourActive={postTourActive}
            onEndPostTour={endPostTour}
          />
        );
      })()}
    </OverlayPanel>
  );
}
